import { buildKeypath, parseKeypath } from '@/data/idGenerator';
import type { Report } from '@/data/schemas';
import {
  SCORECARD_DATE_FIELD_ID,
  SCORECARD_HISTORY_POINTS,
  SCORECARD_SECTION_ID,
  SCORECARD_SERIES_FIELD_IDS,
} from '@/data/scorecardConfig';

type ScorecardHistoryEntry = {
  date: string;
  values: Record<string, number | null>;
};

function scorecardDateToTime(date: string): number {
  const parsed = Date.parse(date);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function coerceScoreValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function extractScorecardEntry(report: Report): ScorecardHistoryEntry {
  const entries = new Map<number, ScorecardHistoryEntry>();

  Object.entries(report.data.values).forEach(([key, value]) => {
    let parsed;
    try {
      parsed = parseKeypath(key);
    } catch {
      return;
    }

    if (parsed.sectionId !== SCORECARD_SECTION_ID) return;
    const index = parsed.indices[0] ?? 0;
    const existing =
      entries.get(index) ?? ({ date: report.meta.visitDate, values: {} } as ScorecardHistoryEntry);

    if (parsed.fieldId === SCORECARD_DATE_FIELD_ID) {
      if (typeof value === 'string' && value.trim().length > 0) {
        existing.date = value;
      }
      entries.set(index, existing);
      return;
    }

    if (!SCORECARD_SERIES_FIELD_IDS.includes(parsed.fieldId)) {
      return;
    }

    existing.values[parsed.fieldId] = coerceScoreValue(value);
    entries.set(index, existing);
  });

  if (entries.size === 0) {
    return {
      date: report.meta.visitDate,
      values: {},
    };
  }

  const lastIndex = Math.max(...entries.keys());
  const entry = entries.get(lastIndex) ?? {
    date: report.meta.visitDate,
    values: {},
  };

  return {
    date: entry.date || report.meta.visitDate,
    values: entry.values ?? {},
  };
}

export function applyScorecardHistory(report: Report, historyReports: Report[]): void {
  const historyEntries = historyReports
    .map((entry) => extractScorecardEntry(entry))
    .filter(Boolean);

  const recentHistory = historyEntries
    .slice()
    .sort((a, b) => scorecardDateToTime(a.date) - scorecardDateToTime(b.date))
    .slice(-SCORECARD_HISTORY_POINTS);

  const lastHistory = recentHistory.at(-1);
  const currentEntry: ScorecardHistoryEntry = {
    date: report.meta.visitDate,
    values: lastHistory ? { ...lastHistory.values } : {},
  };

  const entries = [...recentHistory, currentEntry];

  const cleanedValues: Record<string, unknown> = { ...report.data.values };

  Object.keys(cleanedValues).forEach((keypath) => {
    let parsed;
    try {
      parsed = parseKeypath(keypath);
    } catch {
      return;
    }

    if (parsed.sectionId !== SCORECARD_SECTION_ID) return;
    if (
      parsed.fieldId === SCORECARD_DATE_FIELD_ID ||
      SCORECARD_SERIES_FIELD_IDS.includes(parsed.fieldId)
    ) {
      delete cleanedValues[keypath];
    }
  });

  entries.forEach((entry, index) => {
    const dateKey = buildKeypath(SCORECARD_SECTION_ID, SCORECARD_DATE_FIELD_ID, [index]);
    cleanedValues[dateKey] = entry.date;

    SCORECARD_SERIES_FIELD_IDS.forEach((fieldId) => {
      const valueKey = buildKeypath(SCORECARD_SECTION_ID, fieldId, [index]);
      cleanedValues[valueKey] = entry.values[fieldId] ?? null;
    });
  });

  report.data.values = cleanedValues;
}
